import { Check, Cpu, Loader2, Plus } from 'lucide-react';
import { EntityCard } from '@/components/admin/EntityCard';
import { useCapabilityList } from '@/hooks/use-entity-list';
import { capabilityColors } from '@/lib/capability-colors';

export interface ModelOption {
    id?: string;
    name: string;
    description?: string;
}

interface AgentModelStepProps {
    appKey: string;
    selectedModel: ModelOption | null;
    onSelectModel: (model: ModelOption | null) => void;
    onCreateNew: () => void;
}

export function AgentModelStep({
    appKey,
    selectedModel,
    onSelectModel,
    onCreateNew,
}: AgentModelStepProps) {
    const { data, isLoading } = useCapabilityList(appKey, {
        type: 'model',
        per_page: 100,
    });
    const models: ModelOption[] = data?.data ?? [];

    return (
        <div className="space-y-4">
            <p className="text-center text-sm text-slate-500">
                Model selection is optional. You can skip and configure later.
            </p>

            {isLoading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
            ) : (
                <div>
                    <div
                        className="grid w-full gap-4"
                        style={{
                            gridTemplateColumns:
                                'repeat(auto-fill, minmax(265px, 322px))',
                        }}
                    >
                        {/* Create New Model card */}
                        <button
                            type="button"
                            onClick={onCreateNew}
                            className="text-left"
                        >
                            <EntityCard
                                accentColor="border-l-emerald-500"
                                iconBgColor="bg-emerald-100"
                                iconColor="text-emerald-600"
                                className="border-dashed border-2 hover:border-emerald-400"
                            >
                                <EntityCard.Icon>
                                    <Plus size={18} />
                                </EntityCard.Icon>
                                <EntityCard.Title>Create Model</EntityCard.Title>
                                <EntityCard.Subtitle>
                                    <span className="text-[10px] text-emerald-600">
                                        Configure a new LLM model
                                    </span>
                                </EntityCard.Subtitle>
                                <EntityCard.Description>
                                    Set up a new model with provider and
                                    parameters.
                                </EntityCard.Description>
                            </EntityCard>
                        </button>

                        {/* Existing models */}
                        {models.map((model) => {
                            const isSelected =
                                selectedModel?.name === model.name;
                            return (
                                <button
                                    key={model.id ?? model.name}
                                    type="button"
                                    onClick={() =>
                                        onSelectModel(
                                            isSelected
                                                ? null
                                                : {
                                                      id: model.id,
                                                      name: model.name,
                                                      description:
                                                          model.description,
                                                  },
                                        )
                                    }
                                    className="text-left"
                                >
                                    <EntityCard
                                        selected={isSelected}
                                        accentColor={
                                            capabilityColors.model.border
                                        }
                                        iconBgColor={
                                            capabilityColors.model.bgLight
                                        }
                                        iconColor={
                                            capabilityColors.model.text
                                        }
                                    >
                                        <EntityCard.Icon>
                                            <Cpu size={18} />
                                        </EntityCard.Icon>
                                        <EntityCard.Title>{model.name}</EntityCard.Title>
                                        {model.description && (
                                            <EntityCard.Description>
                                                {model.description}
                                            </EntityCard.Description>
                                        )}
                                        {isSelected && (
                                            <EntityCard.TopRight>
                                                <Check
                                                    size={14}
                                                    className="text-blue-600"
                                                />
                                            </EntityCard.TopRight>
                                        )}
                                    </EntityCard>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {!isLoading && models.length === 0 && (
                <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50">
                    <div className="text-center text-slate-500">
                        <Cpu
                            size={32}
                            className="mx-auto mb-2 text-slate-300"
                        />
                        <p className="text-sm">No models found</p>
                        <p className="mt-1 text-xs">
                            Create a new model to get started
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
